import { Relationship } from '../../types.ts';
import {
  fetchWithTimeout,
  parseErrorResponse,
  type ZelfMelden,
  BULK_FETCH_TIMEOUT,
  relationshipToServer,
  relationshipFromServer,
} from './base.ts';

/** #1825 — TS-spiegel van het `Item` dat de server verwacht (shared/relationshipItems.js). */
export type RelatieItem =
  | {
      kind: 'relative';
      otherPersonId: string;
      newPersonIs: 'parent' | 'child' | 'partner' | 'sibling';
      relationSubtype?: string;
      events?: unknown[];
      parentIds?: string[];
    }
  | {
      kind: 'between';
      personIds: [string, string];
      type: 'spousal';
      relationSubtype?: string;
      events?: unknown[];
    };

export const relationshipsApi = {
  getRelationships: async (projectId: string): Promise<Relationship[]> => {
    // cache: 'no-store' — zie getPeople in people.ts (Refs #912).
    const res = await fetchWithTimeout(`/api/projects/${projectId}/relationships`, {
      cache: 'no-store',
    });
    if (!res.ok) await parseErrorResponse(res);
    const rows = await res.json();
    return rows.map(relationshipFromServer);
  },

  updateRelationship: async (
    relationshipId: string,
    updates: Partial<Relationship>,
    opts?: ZelfMelden
  ): Promise<Relationship> => {
    const payload = relationshipToServer(updates);
    const res = await fetchWithTimeout(`/api/relationships/${relationshipId}`, {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(payload),
    });
    if (!res.ok) await parseErrorResponse(res, opts);
    const data = await res.json();
    return relationshipFromServer(data);
  },

  saveRelationship: async (
    rel: Partial<Relationship>,
    opts?: ZelfMelden
  ): Promise<Relationship> => {
    if (rel.id) {
      return relationshipsApi.updateRelationship(rel.id, rel, opts);
    }
    const serverPayload = relationshipToServer(rel);
    const res = await fetchWithTimeout(`/api/projects/${rel.projectId}/relationships`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(serverPayload),
    });
    if (!res.ok) await parseErrorResponse(res, opts);
    return relationshipFromServer(await res.json());
  },

  /** #1825 — alle relaties van één persoon atomair vastleggen. */
  setRelationships: async (
    projectId: string,
    personId: string,
    items: RelatieItem[],
    opts?: ZelfMelden
  ): Promise<Relationship[]> => {
    const res = await fetchWithTimeout(`/api/projects/${projectId}/relationships/set`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ personId, relationships: items }),
    });
    if (!res.ok) await parseErrorResponse(res, opts);
    const data = await res.json();
    return (data.relationships ?? []).map(relationshipFromServer);
  },

  deleteRelationship: async (relationshipId: string): Promise<void> => {
    const res = await fetchWithTimeout(`/api/relationships/${relationshipId}`, {
      method: 'DELETE',
    });
    if (!res.ok) await parseErrorResponse(res);
  },

  bulkCreateRelationships: async (
    projectId: string,
    relationships: any[]
  ): Promise<{
    created: number;
    relationships?: Array<{ id: string; personId: string; relatedPersonId: string; type: string }>;
  }> => {
    const serverRels = relationships.map((r) => relationshipToServer(r));
    const res = await fetchWithTimeout(
      `/api/projects/${projectId}/relationships/bulk`,
      {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ relationships: serverRels }),
      },
      BULK_FETCH_TIMEOUT
    );
    if (!res.ok) await parseErrorResponse(res);
    const data = await res.json();
    return data;
  },
};
